import { useCallback, useEffect, useState } from 'react';

import { Alert, Aviso, Badge, Button, Card, HelpTip, Input, Switch } from '@/components/ui';
import { api } from '@/lib/api';

import { formatDataHoraSegundos } from '@/lib/format-date';
/**
 * O cartão do Auvo em Configurações › Integrações — **irmão do Omie**, não
 * seção nova. A tela já era uma lista de cartões; este é o terceiro.
 *
 * 🔴 O SELO "SÓ LEITURA" É PARTE DA ENTREGA, não enfeite. A regra do Matheus —
 * *o v2 não escreve no Auvo* — precisa estar visível para quem abre a tela, e
 * não só no código. Quem chegar aqui daqui a um ano tem de saber, sem
 * perguntar, que nada daqui volta para lá.
 *
 * ⚠️ A integração nasce DESLIGADA e sem chave. A tela diz isso com clareza em
 * vez de parecer quebrada: enquanto ninguém preencher, o estado é "esperando a
 * chave", que é diferente de "com problema".
 */

interface AuvoConfig {
  temCredencial: boolean;
  credencialAtualizadaEmIso: string | null;
  credencialAtualizadaPor: string | null;
  intervaloMinutos: number;
  ligada: boolean;
  cotaPiso: number;
}

interface Leitura {
  id: string;
  tipo: string;
  resultado: 'ok' | 'vazio' | 'falha';
  registros: number;
  chamadas: number;
  cotaRestante: number | null;
  erro: string | null;
  completadoEmIso: string | null;
  duracaoMs: number;
}

interface Status {
  config: AuvoConfig;
  ultimaCompletaIso: string | null;
  ultimaTentativaIso: string | null;
  ultimoErro: string | null;
  cotaRestante: number | null;
  guardados: { tarefas: number; clientes: number };
  ultimas: Leitura[];
}

const AJUDA_INTERVALO =
  'De quanto em quanto tempo o IGM lê o Auvo. A cota do Auvo é de cerca de 40 mil ' +
  'chamadas por 30 dias, e o sistema antigo divide a mesma cota — por isso o mínimo ' +
  'é 15 minutos. Uma hora atende bem: o que se lê aqui não é usado em tela ainda.';

interface Webhook {
  url: string | null;
  silencio: { emSilencio: boolean; horasEmSilencio: number | null; limiteHoras: number };
  inscricoes: Array<{ id: number; entity: string; action: string; active: boolean; nossa: boolean }>;
  erro: string | null;
}

const AJUDA_WEBHOOK =
  'Com o webhook, o Auvo avisa o IGM na hora em que algo muda, em vez de o IGM perguntar de ' +
  'tempos em tempos. Registrar a inscrição é a única coisa que o IGM escreve no Auvo — e ele ' +
  'nunca mexe nas inscrições de outros sistemas. A leitura periódica continua como rede de ' +
  'segurança, porque o Auvo desativa inscrição sozinho.';

const AJUDA_SO_LEITURA =
  'O IGM apenas consulta o Auvo. Ele não cria, não altera, não conclui e não comenta ' +
  'nada lá dentro. O Auvo é o sistema que os técnicos usam em campo agora, e uma ' +
  'escrita errada seria um técnico recebendo tarefa que não existe.';

function quando(iso: string | null | undefined): string {
  if (!iso) return 'nunca';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? 'nunca' : formatDataHoraSegundos(d);
}

const RESULTADO: Record<Leitura['resultado'], { rotulo: string; variant: 'success' | 'neutral' | 'danger' }> = {
  ok: { rotulo: 'leu', variant: 'success' },
  vazio: { rotulo: 'nada novo', variant: 'neutral' },
  falha: { rotulo: 'falhou', variant: 'danger' },
};

export function CartaoAuvo() {
  const [status, setStatus] = useState<Status | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState<string | null>(null);

  const [apiKey, setApiKey] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [erroCampo, setErroCampo] = useState<{ campo: string; texto: string } | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [lendo, setLendo] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [webhook, setWebhook] = useState<Webhook | null>(null);

  const carregar = useCallback(() => {
    setErroCarga(null);
    setCarregando(true);
    api
      .get<Status>('/api/v1/admin/auvo/status')
      .then(setStatus)
      .catch((e: Error) => setErroCarga(e.message))
      .finally(() => setCarregando(false));
  }, []);

  const carregarWebhook = useCallback(() => {
    api
      .get<Webhook>(`/api/v1/admin/auvo/webhook?base=${encodeURIComponent(window.location.origin)}`)
      .then(setWebhook)
      .catch(() => setWebhook(null));
  }, []);

  useEffect(carregar, [carregar]);
  useEffect(carregarWebhook, [carregarWebhook]);

  /**
   * 🔴 A ÚNICA escrita do v2 no Auvo, e ela é por botão.
   *
   * ⚠️ Existe porque o painel do Auvo não cria webhook — só a API cria. Registra
   * apenas a inscrição do IGM; as outras nunca são tocadas.
   */
  async function inscrever() {
    setLendo(true);
    setAviso(null);
    try {
      const r = await api.post<{ url: string }>('/api/v1/admin/auvo/webhook/inscrever', {
        base: window.location.origin,
      });
      setAviso(`Webhook registrado no Auvo: ${r.url}`);
      carregarWebhook();
    } catch (err) {
      const e = err as Error & { response?: { erro?: string } };
      setAviso(e.response?.erro ?? e.message);
    } finally {
      setLendo(false);
    }
  }

  /**
   * 🔴 FORMULÁRIO NUNCA É SURDO. Este vai falhar na vida real — chave trocada
   * de campo, espaço colado junto, Auvo fora do ar — e cada caso diz o que é.
   */
  async function salvarCredencial(e: React.FormEvent) {
    e.preventDefault();
    setErroCampo(null);
    setAviso(null);

    if (!apiKey.trim()) {
      setErroCampo({ campo: 'apiKey', texto: 'Cole a chave do Auvo.' });
      document.getElementById('auvo-api-key')?.focus();
      return;
    }
    if (!apiToken.trim()) {
      setErroCampo({ campo: 'apiToken', texto: 'Cole o token do Auvo.' });
      document.getElementById('auvo-api-token')?.focus();
      return;
    }

    setSalvando(true);
    try {
      const r = await api.post<{ config: AuvoConfig }>('/api/v1/admin/auvo/credencial', {
        apiKey: apiKey.trim(),
        apiToken: apiToken.trim(),
      });
      // ⚠️ Os campos são limpos no sucesso: o valor não fica na tela depois de
      // guardado, e não há como lê-lo de volta.
      setApiKey('');
      setApiToken('');
      setStatus((s) => (s ? { ...s, config: r.config } : s));
      setAviso('Chave conferida com o Auvo e guardada. Agora é só ligar a leitura.');
      carregar();
    } catch (err) {
      const e = err as Error & { response?: { campo?: string; erro?: string } };
      const campo = e.response?.campo ?? 'apiKey';
      setErroCampo({ campo, texto: e.response?.erro ?? e.message });
      document.getElementById(campo === 'apiToken' ? 'auvo-api-token' : 'auvo-api-key')?.focus();
    } finally {
      setSalvando(false);
    }
  }

  async function alternar(ligada: boolean) {
    setErroCampo(null);
    setAviso(null);
    try {
      const r = await api.post<{ config: AuvoConfig }>('/api/v1/admin/auvo/ligar', { ligada });
      setStatus((s) => (s ? { ...s, config: r.config } : s));
    } catch (err) {
      const e = err as Error & { response?: { erro?: string } };
      setErroCampo({ campo: 'ligada', texto: e.response?.erro ?? e.message });
    }
  }

  async function salvarIntervalo(minutos: number) {
    setErroCampo(null);
    try {
      const r = await api.post<{ config: AuvoConfig }>('/api/v1/admin/auvo/intervalo', { minutos });
      setStatus((s) => (s ? { ...s, config: r.config } : s));
    } catch (err) {
      const e = err as Error & { response?: { erro?: string } };
      setErroCampo({ campo: 'minutos', texto: e.response?.erro ?? e.message });
    }
  }

  async function lerAgora(tipo: 'clientes' | 'tarefas' | 'usuarios') {
    setLendo(true);
    setAviso(null);
    setErroCampo(null);
    try {
      const r = await api.post<{ leitura: Leitura } & Status>('/api/v1/admin/auvo/ler-agora', { tipo });
      setStatus((s) => (s ? { ...s, ...r } : s));
      setAviso(
        r.leitura.resultado === 'falha'
          ? (r.leitura.erro ?? 'A leitura falhou.')
          : `Leu ${r.leitura.registros} ${tipo === 'usuarios' ? 'técnicos' : tipo} em ${r.leitura.chamadas} chamada(s).`,
      );
    } catch (err) {
      const e = err as Error & { response?: { erro?: string } };
      setAviso(e.response?.erro ?? e.message);
    } finally {
      setLendo(false);
    }
  }

  const cfg = status?.config;
  const selo = !cfg?.temCredencial
    ? { rotulo: 'Esperando a chave', variant: 'neutral' as const }
    : cfg.ligada
      ? { rotulo: 'Lendo', variant: 'success' as const }
      : { rotulo: 'Desligada', variant: 'warning' as const };

  return (
    <Card className="p-4 sm:p-5 space-y-4" data-testid="integracao-auvo">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-xl text-ink-1 dark:text-ink-dark-1">Auvo</h2>
          <p className="text-xs text-ink-3 dark:text-ink-dark-3">
            Ordens de serviço dos técnicos em campo — o IGM lê as tarefas e os clientes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* 🔴 O selo da regra, na tela e não só no código. */}
          <Badge variant="neutral" size="sm" data-testid="auvo-selo-so-leitura">
            Só leitura
          </Badge>
          <HelpTip texto={AJUDA_SO_LEITURA} />
          <Badge variant={selo.variant} size="sm" data-testid="auvo-estado">
            {selo.rotulo}
          </Badge>
        </div>
      </div>

      {carregando && <p className="text-sm text-ink-3 dark:text-ink-dark-3">Consultando…</p>}
      {erroCarga && <Alert variant="danger">{erroCarga}</Alert>}

      {cfg && (
        <>
          {/* ─── Sem chave: o estado normal de quem acabou de subir ─── */}
          {!cfg.temCredencial && (
            // 24/10/2026: aviso em UMA linha (regra de 09/10), o detalhe ao clicar — a tela entrou na trava ao ganhar
            // o botão da jornada dos técnicos.
            <Aviso fato="A leitura do Auvo está desligada: falta a chave" data-testid="auvo-sem-chave">
              Ninguém preencheu a chave ainda. Não é defeito — ela nasce assim, de propósito. Cole a chave e o token
              abaixo para ligar.
            </Aviso>
          )}

          <form onSubmit={salvarCredencial} className="space-y-3" data-testid="auvo-form-credencial">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="auvo-api-key"
                  className="mb-1.5 block text-sm text-ink-2 dark:text-ink-dark-2"
                >
                  Chave do Auvo
                </label>
                <Input
                  id="auvo-api-key"
                  type="password"
                  autoComplete="off"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  error={erroCampo?.campo === 'apiKey' ? erroCampo.texto : undefined}
                  placeholder={cfg.temCredencial ? 'Guardada — preencha só para trocar' : 'Cole aqui'}
                />
              </div>
              <div>
                <label
                  htmlFor="auvo-api-token"
                  className="mb-1.5 block text-sm text-ink-2 dark:text-ink-dark-2"
                >
                  Token do Auvo
                </label>
                <Input
                  id="auvo-api-token"
                  type="password"
                  autoComplete="off"
                  value={apiToken}
                  onChange={(e) => setApiToken(e.target.value)}
                  error={erroCampo?.campo === 'apiToken' ? erroCampo.texto : undefined}
                  placeholder={cfg.temCredencial ? 'Guardado — preencha só para trocar' : 'Cole aqui'}
                />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" size="sm" loading={salvando} data-testid="auvo-salvar-credencial">
                Conferir e guardar
              </Button>
              <span className="text-xs text-ink-3 dark:text-ink-dark-3">
                {cfg.temCredencial
                  ? `Guardada e cifrada. Trocada em ${quando(cfg.credencialAtualizadaEmIso)}.`
                  : 'A chave é conferida com o Auvo antes de ser guardada, e fica cifrada no cofre.'}
              </span>
            </div>
          </form>

          {aviso && <Alert variant="info" data-testid="auvo-aviso">{aviso}</Alert>}
          {erroCampo?.campo === 'ligada' && <Alert variant="danger">{erroCampo.texto}</Alert>}

          {/* ─── Liga/desliga e cadência ─── */}
          <div className="flex flex-wrap items-center gap-4 border-t border-line pt-4 dark:border-line-dark">
            <Switch
              checked={cfg.ligada}
              onChange={(e) => void alternar(e.target.checked)}
              label="Ler o Auvo automaticamente"
              data-testid="auvo-ligar"
            />
            <label className="flex items-center gap-2 text-sm text-ink-2 dark:text-ink-dark-2">
              A cada
              <Input
                type="number"
                min={15}
                max={1440}
                className="w-24"
                defaultValue={cfg.intervaloMinutos}
                onBlur={(e) => salvarIntervalo(Number(e.target.value))}
                error={erroCampo?.campo === 'minutos' ? erroCampo.texto : undefined}
                data-testid="auvo-intervalo"
              />
              minutos
              <HelpTip texto={AJUDA_INTERVALO} />
            </label>
          </div>

          {/* ─── O carimbo: quando a leitura COMPLETOU ─── */}
          <div className="grid gap-2 text-xs text-ink-3 dark:text-ink-dark-3 sm:grid-cols-2">
            <span data-testid="auvo-carimbo">
              Última leitura concluída: <strong>{quando(status.ultimaCompletaIso)}</strong>
            </span>
            <span>
              Guardado até agora: {status.guardados.tarefas} tarefas · {status.guardados.clientes}{' '}
              clientes
            </span>
            {status.cotaRestante !== null && (
              <span>
                Cota do Auvo: {status.cotaRestante.toLocaleString('pt-BR')} chamadas restantes (o
                IGM para em {cfg.cotaPiso.toLocaleString('pt-BR')})
              </span>
            )}
          </div>

          {status.ultimoErro && (
            <Alert variant="warning" data-testid="auvo-ultimo-erro">
              Na última falha: {status.ultimoErro}
            </Alert>
          )}

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" loading={lendo} onClick={() => lerAgora('clientes')}>
              Ler clientes agora
            </Button>
            <Button variant="outline" size="sm" loading={lendo} onClick={() => lerAgora('tarefas')}>
              Ler tarefas agora
            </Button>
            {/* 24/10/2026: a jornada dos técnicos vem do cadastro de usuário do Auvo e é lida 1×/dia; este botão lê na
                hora, depois de alguém preencher a jornada lá. */}
            <Button variant="outline" size="sm" loading={lendo} onClick={() => lerAgora('usuarios')}>
              Ler técnicos (jornada) agora
            </Button>
          </div>

          {status.ultimas.length > 0 && (
            <details className="text-xs" data-testid="auvo-historico">
              <summary className="cursor-pointer text-ink-2 dark:text-ink-dark-2">
                As últimas {status.ultimas.length} leituras
              </summary>
              <ul className="mt-2 space-y-1">
                {status.ultimas.map((l) => (
                  <li key={l.id} className="flex flex-wrap items-center gap-2">
                    <Badge variant={RESULTADO[l.resultado].variant} size="sm">
                      {RESULTADO[l.resultado].rotulo}
                    </Badge>
                    <span className="text-ink-3 dark:text-ink-dark-3">
                      {l.tipo} · {quando(l.completadoEmIso)} · {l.registros} registros ·{' '}
                      {l.chamadas} chamadas
                    </span>
                    {l.erro && <span className="text-igm">{l.erro}</span>}
                  </li>
                ))}
              </ul>
            </details>
          )}

          {/* ─── O webhook: o que faz a leitura deixar de ser de 15 em 15 min ─── */}
          <div className="border-t border-line pt-3 dark:border-line-dark" data-testid="auvo-webhook">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-ink-1 dark:text-ink-dark-1">
                Webhook do Auvo
              </span>
              <HelpTip texto={AJUDA_WEBHOOK} />
              {webhook?.silencio.emSilencio && (
                <Badge variant="danger" size="sm" data-testid="auvo-webhook-silencio">
                  sem eventos há {webhook.silencio.horasEmSilencio} horas
                </Badge>
              )}
            </div>

            {/* 🔴 Silêncio prolongado é inscrição derrubada, não semana calma. */}
            {webhook?.silencio.emSilencio && (
              <Aviso
                tom="perigo"
                className="mt-2"
                fato={`Nenhum evento do Auvo nas últimas ${webhook.silencio.horasEmSilencio} horas`}
              >
                O Auvo desativa inscrição por conta própria — confira abaixo se a do IGM ainda está ativa.
              </Aviso>
            )}

            <p className="mt-2 text-xs text-ink-3 dark:text-ink-dark-3">
              {webhook?.url
                ? `O Auvo chama: ${webhook.url}`
                : 'Endereço público do IGM não configurado — sem ele o Auvo não tem para onde chamar.'}
            </p>

            {webhook && webhook.inscricoes.length > 0 && (
              <ul className="mt-2 space-y-1 text-xs" data-testid="auvo-inscricoes">
                {webhook.inscricoes.map((i) => (
                  <li key={i.id} className="flex flex-wrap items-center gap-2">
                    <Badge variant={i.nossa ? 'success' : 'neutral'} size="sm">
                      {i.nossa ? 'do IGM' : 'de outro sistema'}
                    </Badge>
                    <span className="text-ink-3 dark:text-ink-dark-3">
                      {i.entity} · {i.action} · {i.active ? 'ativa' : 'inativa'}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                loading={lendo}
                onClick={inscrever}
                data-testid="auvo-inscrever"
              >
                Registrar o webhook do IGM no Auvo
              </Button>
              <span className="text-xs text-ink-3 dark:text-ink-dark-3">
                Registra só a inscrição do IGM. As outras não são tocadas.
              </span>
            </div>
          </div>

          <p className="text-xs text-ink-3 dark:text-ink-dark-3">
            O que é lido alimenta a fila de Orçamentos e a lista de Chamados, na Manutenção. Ligar as
            tarefas do Auvo aos clientes do IGM é o passo seguinte, e ele não foi dado.
          </p>
        </>
      )}
    </Card>
  );
}
